import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Textarea } from "@runcheetah/ui";
import { Pencil, Plus, Trash2 } from "lucide-react";

type SaveState = "idle" | "saving" | "saved" | "error";

export type EditableListFieldProps = {
  label: string;
  description?: string;
  items: string[] | null | undefined;
  onSave: (items: string[]) => Promise<unknown> | unknown;
  emptyStateLabel?: string;
  addLabel?: string;
};

export function EditableListField({
  label,
  description,
  items,
  onSave,
  emptyStateLabel,
  addLabel,
}: EditableListFieldProps) {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  const [drafts, setDrafts] = useState<string[]>([]);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const handleEdit = () => {
    setDrafts(items?.length ? [...items] : [""]);
    setEditing(true);
    setSaveState("idle");
  };

  const handleSave = async () => {
    const next = drafts.map((d) => d.trim()).filter(Boolean);

    setSaveState("saving");

    try {
      await onSave(next);
      setSaveState("saved");
      setEditing(false);
    } catch {
      setSaveState("error");
    }
  };

  const setDraft = (i: number, value: string) =>
    setDrafts((prev) => prev.map((d, idx) => (idx === i ? value : d)));

  const addDraft = () => {
    setDrafts((prev) => [...prev, ""]);
  };

  const removeDraft = (index: number) => {
    setDrafts((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleCancel = () => {
    setDrafts([]);
    setEditing(false);
    setSaveState("idle");
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {label}
          </p>
          {description ? (
            <p className="text-xs leading-5 text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {!editing && (
          <button
            type="button"
            onClick={handleEdit}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Pencil className="h-3 w-3" />
            <FormattedMessage id="common.edit" defaultMessage="Edit" />
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          {drafts.map((draft, i) => (
            <div key={i} className="flex items-start gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(i, e.target.value)}
                className="min-h-12 flex-1 resize-y text-sm"
                placeholder={intl.formatMessage(
                  { id: "editableListField.itemPlaceholder", defaultMessage: "{label} {number}" },
                  { label, number: i + 1 },
                )}
              />
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={() => removeDraft(i)}
                className="mt-1 shrink-0"
                aria-label={intl.formatMessage(
                  {
                    id: "editableListField.removeItemAria",
                    defaultMessage: "Remove {label} {number}",
                  },
                  { label, number: i + 1 },
                )}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={addDraft}>
              <Plus className="mr-1 h-4 w-4" />
              {addLabel ?? (
                <FormattedMessage id="editableListField.addItem" defaultMessage="Add item" />
              )}
            </Button>
            <Button type="button" size="sm" onClick={handleSave} disabled={saveState === "saving"}>
              {saveState === "saving" ? (
                <FormattedMessage id="common.saving" defaultMessage="Saving..." />
              ) : (
                <FormattedMessage id="common.save" defaultMessage="Save" />
              )}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={handleCancel}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
          </div>
        </div>
      ) : items?.length ? (
        <ul className="space-y-1 list-disc list-inside text-sm marker:text-muted-foreground">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {emptyStateLabel ?? (
            <FormattedMessage id="editableListField.empty" defaultMessage="No items yet" />
          )}
        </p>
      )}
      {saveState !== "idle" ? (
        <p className="text-xs text-muted-foreground">
          {saveState === "saved" ? (
            <FormattedMessage id="common.saved" defaultMessage="Saved" />
          ) : null}
          {saveState === "error" ? (
            <FormattedMessage
              id="common.saveErrorTryAgain"
              defaultMessage="Could not save. Try again."
            />
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
